
	import { Observer } from 'mobx-vue';
	import { Component, InjectReactive, Provide, Vue } from 'vue-property-decorator';
	import CategoryContainer from '@/components/new-prescription/prescription-categories/CategoryContainer.vue';
	import ShellModifications from '@/components/new-prescription/prescription-categories/ShellModifications.vue';
	import PostingOptions from '@/components/new-prescription/prescription-categories/PostingOptions.vue';
	import TopCovers from '@/components/new-prescription/prescription-categories/TopCovers.vue';
	import Accommodations from '@/components/new-prescription/prescription-categories/Accommodations.vue';
	import HelpModal from '@/views/prescription/components/HelpModal.vue';
	import { autorun, IReactionDisposer, reaction } from 'mobx';
	import Loading from '@/components/Loading.vue';

	export class PrescriptionEventHandler {
		private handlers: Array<IReactionDisposer> = [];
		private initializationFunctions: Array<Function> = [];
		private previousValues = {};
		private previousValuesWatchers: Array<IReactionDisposer> = [];

		public addInitializationFunction(initializationFactory: Function) {
			this.initializationFunctions.push(initializationFactory);
		}

		public addEventHandler<T extends Record<string, any>, K extends keyof T>(
			watch: T,
			property: K,
			condition: typeof watch[typeof property] | typeof watch[typeof property][],
			result: (value: T[K], previousValue: T[K]) => void
		) {
			if (property in watch) {
				if (Array.isArray(condition)) {
					this.handlers.push(
						(() => {
							const id = Object.keys(this.previousValues).length;
							this.previousValues[id] = watch[property];

							this.previousValuesWatchers.push(
								reaction(
									() => watch[property],
									(val) => {
										if (!condition.includes(val)) {
											this.previousValues[id] = watch[property];
										}
									}
								)
							);

							return autorun(() => {
								if (condition.includes(watch[property]))
									setTimeout(() => {
										result(watch[property], this.previousValues[id]);
										this.previousValues[id] = watch[property];
									}, 0);
							});
						})()
					);
				} else {
					this.handlers.push(
						(() => {
							const id = Object.keys(this.previousValues).length;
							this.previousValues[id] = watch[property];

							this.previousValuesWatchers.push(
								reaction(
									() => watch[property],
									(val) => {
										if (val !== condition) {
											this.previousValues[id] = watch[property];
										}
									}
								)
							);

							return autorun(() => {
								if (watch[property] === condition)
									setTimeout(() => {
										result(watch[property], this.previousValues[id]);
										this.previousValues[id] = watch[property];
									}, 0);
							});
						})()
					);
				}
			}
		}

		public start() {
			this.initializationFunctions.forEach((fn) => fn());
		}

		public stop() {
			this.handlers.forEach((fn) => fn());
			this.previousValuesWatchers.forEach((fn) => fn());
			this.handlers = [];
			this.previousValues = {};
			this.previousValuesWatchers = [];
		}

		public restart() {
			this.stop();
			this.start();
		}
	}

	@Observer
	@Component({
		components: {
			Loading,
			HelpModal,
			Accommodations,
			TopCovers,
			PostingOptions,
			ShellModifications,
			CategoryContainer
		}
	})
	export default class PrescriptionForm extends Vue {
		@InjectReactive() hamburgerView!: boolean;
		@Provide() eventHandler = new PrescriptionEventHandler();

		activated() {
			window.onpageshow = function(event) {
				let params = new URL(document.location.href).searchParams;
				let name = params.get('id');
				if (event.persisted && name) {
					window.location.reload();
				}
			};
			this.eventHandler.start();

			reaction(
				() => this.$state.prescription.newPrescription,
				() => {
					this.eventHandler.restart();
				}
			);
		}
		deactivated() {
			this.eventHandler.stop();
		}
	}
